/**
 * ClientPicker — Sélecteur de client réutilisable pour les flows admin.
 *
 * Affiche un input de recherche + dropdown des clients filtrés (live).
 * Si aucun client ne matche : bouton "Créer ce client" qui ouvre un formulaire
 * d'inscription rapide (mêmes champs que l'inscription publique : prénom, nom,
 * email, téléphone, adresse optionnelle).
 *
 * Le compte créé n'a pas de mot de passe → le client devra utiliser
 * "Mot de passe oublié" sur le site pour finaliser son inscription.
 *
 * Props :
 *  - clients: tableau de clients connus (depuis /api/clients)
 *  - selected: objet client actuellement sélectionné (ou null)
 *  - onSelect(client): callback quand un client est choisi
 *  - onCreated(client): callback quand un nouveau client vient d'être créé (idem onSelect)
 *  - placeholder: texte du champ recherche
 *  - dataTestidPrefix: préfixe pour les data-testid (pour différencier les multiples instances)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, X, UserPlus, Check, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const normalize = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export function ClientPicker({ clients = [], selected = null, onSelect, onCreated, placeholder = "Rechercher un client…", dataTestidPrefix = "client-picker" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address_line_1: "", postal_code: "", city: "" });
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return clients.slice(0, 8);
    return clients
      .filter(c => normalize(c.name).includes(q) || normalize(c.email).includes(q) || normalize(c.phone || "").includes(q))
      .slice(0, 12);
  }, [clients, query]);

  const clear = () => { onSelect(null); setQuery(""); setShowCreate(false); };

  const pick = (c) => {
    onSelect(c);
    setQuery("");
    setOpen(false);
    setShowCreate(false);
  };

  const openCreate = () => {
    // Pré-remplit l'email si la recherche ressemble à un email
    const guess = query.trim();
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guess);
    setForm({
      first_name: looksLikeEmail ? "" : guess.split(" ")[0] || "",
      last_name: looksLikeEmail ? "" : (guess.split(" ").slice(1).join(" ") || ""),
      email: looksLikeEmail ? guess : "",
      phone: "",
      address_line_1: "",
      postal_code: "",
      city: "",
    });
    setShowCreate(true);
    setOpen(false);
  };

  const submitCreate = async (e) => {
    e?.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Prénom, email et téléphone obligatoires.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/clients`, form, { withCredentials: true });
      const u = data.user;
      // Convertit le user en format "client" pour la liste
      const clientEntry = {
        ...u,
        appointment_count: 0,
        order_count: 0,
      };
      toast.success(data.created ? "Client créé ✨" : "Compte existant réutilisé.");
      setShowCreate(false);
      setQuery("");
      onSelect(clientEntry);
      if (onCreated) onCreated(clientEntry);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible");
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return (
      <div className="space-y-1" ref={containerRef} data-testid={`${dataTestidPrefix}-selected`}>
        <Label className="text-xs text-[#B59ED1]">Client</Label>
        <div className="flex items-center gap-2 p-2.5 bg-[#1B132B] border border-[#00E5FF]/30 rounded-md">
          <Check className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{selected.name}</p>
            <p className="text-[11px] text-[#B59ED1] truncate">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</p>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-[#B59ED1] hover:text-white p-1"
            aria-label="Changer de client"
            data-testid={`${dataTestidPrefix}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1 relative" ref={containerRef}>
      <Label className="text-xs text-[#B59ED1]">Client</Label>
      {showCreate ? (
        <form onSubmit={submitCreate} className="p-3 bg-[#1B132B] border border-[#D926A9]/30 rounded-md space-y-2.5" data-testid={`${dataTestidPrefix}-create-form`}>
          <p className="text-xs text-[#D926A9] flex items-center gap-1.5 font-medium">
            <UserPlus className="w-3.5 h-3.5" /> Nouveau client
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Prénom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm" data-testid={`${dataTestidPrefix}-first-name`} required />
            <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm" data-testid={`${dataTestidPrefix}-last-name`} />
          </div>
          <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm" data-testid={`${dataTestidPrefix}-email`} required />
          <Input type="tel" placeholder="Téléphone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm" data-testid={`${dataTestidPrefix}-phone`} required />
          <Input placeholder="Adresse (optionnel)" value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="CP" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm col-span-1" />
            <Input placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#261B3B] border-white/10 h-9 text-sm col-span-2" />
          </div>
          <p className="text-[10px] text-[#B59ED1] leading-snug">
            Le client devra cliquer sur "Mot de passe oublié" lors de sa première connexion sur le site pour définir son mot de passe.
          </p>
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={creating} size="sm" className="flex-1 bg-[#D926A9] hover:bg-[#D926A9]/90 text-white text-xs" data-testid={`${dataTestidPrefix}-create-submit`}>
              {creating ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Création…</> : "Créer le client"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowCreate(false)} className="border-white/10 text-[#E2D5F8] text-xs">
              Annuler
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="relative">
            <Input
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="bg-[#1B132B] border-white/10 pl-9 h-10"
              data-testid={`${dataTestidPrefix}-search`}
              autoComplete="off"
            />
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B59ED1] pointer-events-none" />
          </div>
          {open && (
            <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-[#261B3B] border border-white/10 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid={`${dataTestidPrefix}-dropdown`}>
              {filtered.length > 0 ? (
                <ul className="py-1">
                  {filtered.map((c) => (
                    <li key={c.user_id}>
                      <button
                        type="button"
                        onClick={() => pick(c)}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors flex items-center gap-2"
                        data-testid={`${dataTestidPrefix}-option-${c.user_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{c.name}</p>
                          <p className="text-[11px] text-[#B59ED1] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-2 text-xs text-[#B59ED1]">Aucun client ne correspond.</p>
              )}
              <div className="border-t border-white/10 p-2">
                <button
                  type="button"
                  onClick={openCreate}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-[#D926A9] hover:bg-[#D926A9]/10 rounded transition-colors"
                  data-testid={`${dataTestidPrefix}-create-btn`}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Créer un nouveau client{query ? ` "${query}"` : ""}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
